import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Document, Font, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import sarabun from "fonts/SarabunRegular.ttf";
import sarabunBold from "fonts/SarabunSemiBold.ttf";
import phetsarath from "fonts/PhetsarathRegular.ttf";
import phetsarathBold from "fonts/PhetsarathBold.ttf";
Font.register({
    family: "Sarabun",
    fonts: [
        {
            src: sarabun
        },
        {
            src: sarabunBold,
            fontWeight: "bold"
        }
    ]
});
Font.register({
    family: "Phetsarath",
    fonts: [
        {
            src: phetsarath
        },
        {
            src: phetsarathBold,
            fontWeight: "bold"
        }
    ]
});
var BRAND_RED = "#b00412";
var styles = StyleSheet.create({
    page: {
        paddingTop: 12,
        paddingHorizontal: 12,
        paddingBottom: 24,
        fontFamily: [
            "Sarabun",
            "Phetsarath"
        ],
        fontSize: 8,
        color: "#151515"
    },
    logoWrap: {
        height: 36,
        justifyContent: "center",
        marginBottom: 5
    },
    logo: {
        width: 82,
        height: 30,
        objectFit: "contain"
    },
    customerRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 6
    },
    customerText: {
        fontSize: 9,
        marginRight: 17
    },
    orderDateLine: {
        display: "flex",
        width: 90,
        marginLeft: 3,
        borderBottomWidth: 0.6,
        borderBottomColor: "#333333",
        top: 4
    },
    table: {
        borderTopWidth: 0.5,
        borderLeftWidth: 0.5,
        borderColor: "#dddddd"
    },
    row: {
        flexDirection: "row",
        minHeight: 78
    },
    headerRow: {
        flexDirection: "row",
        minHeight: 34,
        backgroundColor: BRAND_RED
    },
    cell: {
        borderRightWidth: 0.5,
        borderBottomWidth: 0.5,
        borderColor: "#dddddd",
        paddingHorizontal: 4,
        paddingVertical: 4,
        justifyContent: "center"
    },
    headerCell: {
        borderRightWidth: 0.5,
        borderRightColor: "#ca2531",
        paddingHorizontal: 3,
        paddingVertical: 4,
        justifyContent: "center"
    },
    headerText: {
        color: "#ffffff",
        fontSize: 8.5,
        fontWeight: "bold",
        textAlign: "center"
    },
    center: {
        textAlign: "center"
    },
    imageWrap: {
        alignItems: "center",
        justifyContent: "center"
    },
    productImage: {
        width: 62,
        height: 62,
        objectFit: "contain"
    },
    productNameTh: {
        fontSize: 9,
        fontWeight: "bold",
        lineHeight: 1.25
    },
    productNameSecondary: {
        color: "#666666",
        fontSize: 7.5,
        lineHeight: 1.25,
        marginTop: 3
    },
    sku: {
        fontSize: 8,
        fontWeight: "bold",
        marginTop: 5
    },
    unitLine: {
        minHeight: 21,
        justifyContent: "center",
        paddingVertical: 2
    },
    unitSeparator: {
        borderTopWidth: 0.35,
        borderTopColor: "#eeeeee"
    },
    barcodeCell: {
        backgroundColor: "#fafafa"
    },
    barcodeText: {
        fontSize: 8.5,
        textAlign: "center"
    },
    packRow: {
        flexDirection: "row",
        alignItems: "center"
    },
    packUnit: {
        width: 34,
        fontWeight: "bold"
    },
    packEqual: {
        width: 14,
        textAlign: "center"
    },
    packAmount: {
        flexGrow: 1
    },
    quantityBox: {
        height: 17,
        marginHorizontal: 5,
        borderWidth: 0.6,
        borderColor: "#d3d3d3",
        borderRadius: 2,
        backgroundColor: "#ffffff"
    },
    empty: {
        padding: 24,
        textAlign: "center",
        color: "#666666"
    },
    pageNumber: {
        position: "absolute",
        right: 12,
        bottom: 8,
        fontSize: 7,
        color: "#777777"
    }
});
var widths = {
    index: "6%",
    image: "13%",
    product: "28%",
    barcode: "18%",
    pack: "19%",
    quantity: "17%"
};
var normalizeImage = function(image) {
    if (typeof image !== "string" || !image.trim()) return null;
    var value = image.trim();
    if (/^(https?:|data:|blob:)/i.test(value)) return value;
    if (value.startsWith("/") && typeof window !== "undefined") {
        return "".concat(window.location.origin).concat(value);
    }
    return null;
};
var getLogo = function() {
    return typeof window !== "undefined" ? "".concat(window.location.origin, "/images/pardesco-logo-new-red-200.png") : null;
};
var LAO_UNIT_NAMES = {
    ชิ้น: "ຊິ້ນ",
    แพ็ค: "ແພັກ",
    แพ็ก: "ແພັກ",
    ลัง: "ແກັດ"
};
var getLaoUnitName = function(unitName) {
    var normalizedName = String(unitName || "").trim();
    return LAO_UNIT_NAMES[normalizedName] || normalizedName || "ໜ່ວຍ";
};
var getPackAmountText = function(unit) {
    var ratio = Number((unit === null || unit === void 0 ? void 0 : unit.ratio) || 0);
    var ratioOption = Number((unit === null || unit === void 0 ? void 0 : unit.ratio_option) || 0);
    var pieces = ratio > 0 ? "".concat(ratio, " ຊິ້ນ") : "-";
    return ratioOption > 0 ? "".concat(pieces, " / ").concat(ratioOption, " ແພັກ") : pieces;
};
var UnitLines = function(param) {
    var units = param.units, render = param.render;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: ((units === null || units === void 0 ? void 0 : units.length) ? units : [
            {}
        ]).map(function(unit, index) {
            return /*#__PURE__*/ _jsx(View, {
                style: [
                    styles.unitLine,
                    index > 0 ? styles.unitSeparator : null
                ],
                children: render(unit, index)
            }, "".concat((unit === null || unit === void 0 ? void 0 : unit.barcode) || "unit", "-").concat(index));
        })
    });
};
export function BranchOrderNoteDocument(param) {
    var data = param.data;
    var branch = (data === null || data === void 0 ? void 0 : data.branch) || {};
    var products = Array.isArray(data === null || data === void 0 ? void 0 : data.products) ? data.products : [];
    var logo = getLogo();
    var customerName = branch.branch_name ? "".concat(branch.shop_name || "", " (").concat(branch.branch_name, ") ") : branch.shop_name || "-";
    return /*#__PURE__*/ _jsx(Document, {
        children: /*#__PURE__*/ _jsxs(Page, {
            size: "A4",
            orientation: "portrait",
            style: styles.page,
            children: [
                /*#__PURE__*/ _jsx(View, {
                    style: styles.logoWrap,
                    children: logo ? /*#__PURE__*/ _jsx(Image, {
                        src: logo,
                        style: styles.logo
                    }) : null
                }),
                /*#__PURE__*/ _jsxs(View, {
                    style: styles.customerRow,
                    children: [
                        /*#__PURE__*/ _jsxs(Text, {
                            style: styles.customerText,
                            children: [
                                "ລູກຄ້າ: ",
                                customerName
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Text, {
                            style: styles.customerText,
                            children: [
                                "ພະນັກງານຂາຍຜູ້ດູແລ: ",
                                branch.sale_name || "-"
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Text, {
                            style: styles.customerText,
                            children: "ວັນທີສັ່ງຊື້:"
                        }),
                        /*#__PURE__*/ _jsx(View, {
                            style: styles.orderDateLine
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(View, {
                    style: styles.table,
                    children: [
                        /*#__PURE__*/ _jsxs(View, {
                            style: styles.headerRow,
                            fixed: true,
                            children: [
                                /*#__PURE__*/ _jsx(View, {
                                    style: [
                                        styles.headerCell,
                                        {
                                            width: widths.index
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Text, {
                                        style: styles.headerText,
                                        children: "ລຳດັບ"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(View, {
                                    style: [
                                        styles.headerCell,
                                        {
                                            width: widths.image
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Text, {
                                        style: styles.headerText,
                                        children: "ຮູບພາບສິນຄ້າ"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(View, {
                                    style: [
                                        styles.headerCell,
                                        {
                                            width: widths.product
                                        }
                                    ],
                                    children: [
                                        /*#__PURE__*/ _jsx(Text, {
                                            style: styles.headerText,
                                            children: "ຊື່ສິນຄ້າ"
                                        }),
                                        /*#__PURE__*/ _jsx(Text, {
                                            style: styles.headerText,
                                            children: "ລະຫັດສິນຄ້າ (SKU)"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(View, {
                                    style: [
                                        styles.headerCell,
                                        {
                                            width: widths.barcode
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Text, {
                                        style: styles.headerText,
                                        children: "ບາໂຄດ"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(View, {
                                    style: [
                                        styles.headerCell,
                                        {
                                            width: widths.pack
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Text, {
                                        style: styles.headerText,
                                        children: "ຂະໜາດບັນຈຸ"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(View, {
                                    style: [
                                        styles.headerCell,
                                        {
                                            width: widths.quantity
                                        }
                                    ],
                                    children: [
                                        /*#__PURE__*/ _jsx(Text, {
                                            style: styles.headerText,
                                            children: "ຈຳນວນທີ່ຕ້ອງການ"
                                        }),
                                        /*#__PURE__*/ _jsx(Text, {
                                            style: [
                                                styles.headerText,
                                                {
                                                    fontSize: 6.5
                                                }
                                            ],
                                            children: "(ໜ່ວຍນັບຕາມຂະໜາດບັນຈຸ)"
                                        })
                                    ]
                                })
                            ]
                        }),
                        products.length ? products.map(function(product, productIndex) {
                            var image = normalizeImage(product.product_image);
                            var units = product.units || [];
                            var secondaryName = product.product_name_en || product.product_name_la || "";
                            return /*#__PURE__*/ _jsxs(View, {
                                style: styles.row,
                                wrap: false,
                                children: [
                                    /*#__PURE__*/ _jsx(View, {
                                        style: [
                                            styles.cell,
                                            {
                                                width: widths.index
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Text, {
                                            style: styles.center,
                                            children: productIndex + 1
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(View, {
                                        style: [
                                            styles.cell,
                                            styles.imageWrap,
                                            {
                                                width: widths.image
                                            }
                                        ],
                                        children: image ? /*#__PURE__*/ _jsx(Image, {
                                            src: image,
                                            style: styles.productImage
                                        }) : null
                                    }),
                                    /*#__PURE__*/ _jsxs(View, {
                                        style: [
                                            styles.cell,
                                            {
                                                width: widths.product
                                            }
                                        ],
                                        children: [
                                            /*#__PURE__*/ _jsx(Text, {
                                                style: styles.productNameTh,
                                                children: product.product_name_la || secondaryName || "-"
                                            }),
                                            secondaryName && secondaryName !== product.product_name_th ? /*#__PURE__*/ _jsx(Text, {
                                                style: styles.productNameSecondary,
                                                children: secondaryName
                                            }) : null,
                                            /*#__PURE__*/ _jsx(Text, {
                                                style: styles.sku,
                                                children: product.product_code || "-"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(View, {
                                        style: [
                                            styles.cell,
                                            styles.barcodeCell,
                                            {
                                                width: widths.barcode
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(UnitLines, {
                                            units: units,
                                            render: function(unit) {
                                                return /*#__PURE__*/ _jsx(Text, {
                                                    style: styles.barcodeText,
                                                    children: unit.barcode || "-"
                                                });
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(View, {
                                        style: [
                                            styles.cell,
                                            {
                                                width: widths.pack
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(UnitLines, {
                                            units: units,
                                            render: function(unit) {
                                                return /*#__PURE__*/ _jsxs(View, {
                                                    style: styles.packRow,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Text, {
                                                            style: styles.packUnit,
                                                            children: getLaoUnitName(unit.unit_name)
                                                        }),
                                                        /*#__PURE__*/ _jsx(Text, {
                                                            style: styles.packEqual,
                                                            children: "="
                                                        }),
                                                        /*#__PURE__*/ _jsx(Text, {
                                                            style: styles.packAmount,
                                                            children: getPackAmountText(unit)
                                                        })
                                                    ]
                                                });
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(View, {
                                        style: [
                                            styles.cell,
                                            {
                                                width: widths.quantity
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(UnitLines, {
                                            units: units,
                                            render: function() {
                                                return /*#__PURE__*/ _jsx(View, {
                                                    style: styles.quantityBox
                                                });
                                            }
                                        })
                                    })
                                ]
                            }, product.product_id || productIndex);
                        }) : /*#__PURE__*/ _jsx(Text, {
                            style: styles.empty,
                            children: "ບໍ່ພົບປະຫວັດສິນຄ້າທີ່ເຄີຍສັ່ງຊື້"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Text, {
                    fixed: true,
                    style: styles.pageNumber,
                    render: function(param) {
                        var pageNumber = param.pageNumber, totalPages = param.totalPages;
                        return "ໜ້າ ".concat(pageNumber, " / ").concat(totalPages);
                    }
                })
            ]
        })
    });
}
